// Commentaires des clients — trois niveaux, un fil, un auteur (fiche I du 06/10 ; #563, 08/10).
//
// Le navigateur n'envoie que des IDENTIFIANTS d'ancre (page, section, graphique, libellé de
// tuile) : le serveur valide chacun contre ses registres et le code des builders, et construit
// lui-même le contexte (titres, commune, date). Le texte part brut ; il revient brut et React
// l'échappe à l'affichage — jamais de `dangerouslySetInnerHTML` dans ce dossier.
//
// Les gestes permis sur un message (modifier, retirer, répondre) sont SERVIS par le serveur
// (`peut_modifier`, `peut_retirer`, `peut_repondre`) : l'écran ne les devine pas, il les montre.
import axios from 'axios'
import { NOM_PRODUIT } from '../../constants'
import { API } from '../types'

export type Nature = 'erreur' | 'question' | 'suggestion'
export type Statut = 'recu' | 'en_cours' | 'traite' | 'clos_par_client' | 'archive'
export type EtatTickets = 'ouverts' | 'clos'
export const estClos = (m: Message) => m.statut === 'clos_par_client' || m.statut === 'archive'
export type Niveau = 'moi' | 'equipe' | 'terobs'

export const NATURES: { v: Nature; label: string }[] = [
  { v: 'erreur', label: 'Signaler une erreur' },
  { v: 'question', label: 'Poser une question' },
  { v: 'suggestion', label: 'Suggérer une amélioration' },
]
export const STATUTS: Record<Statut, string> = { recu: 'Reçu', en_cours: 'En cours', traite: 'Traité',
  clos_par_client: 'Clos par le client', archive: 'Archivé' }
export const TAILLE_MAX = 4000

/** Les trois niveaux de partage (CDC I, § 4) : qui lit, ce que dit le bouton d'envoi, le badge. */
export const NIVEAUX: Record<Niveau, { titre: string; badge: string; qui: string; envoyer: string
                                      ; envoye: string }> = {
  moi: {
    titre: 'Pour moi', badge: 'Privé',
    qui: `Vous seul. Ni vos collègues ni l’équipe ${NOM_PRODUIT} n’y ont accès.`,
    envoyer: 'Enregistrer ma note', envoye: 'Note enregistrée. Vous seul la lirez.',
  },
  equipe: {
    titre: 'Mon équipe', badge: 'Équipe',
    qui: `Les membres de l’espace de votre commune qui ont accès à cette page. L’équipe ${NOM_PRODUIT} n’y a pas accès.`,
    envoyer: 'Publier pour l’équipe', envoye: 'Message publié pour votre équipe.',
  },
  terobs: {
    titre: `À ${NOM_PRODUIT}`, badge: NOM_PRODUIT,
    qui: `L’équipe ${NOM_PRODUIT} le lira et pourra vous répondre. Vos collègues ne le verront pas.`,
    envoyer: `Envoyer à ${NOM_PRODUIT}`,
    envoye: `Message envoyé à l’équipe ${NOM_PRODUIT}. Sa réponse s’affichera ici.`,
  },
}

/** Le niveau proposé d'office à l'écriture (#563, consigne du 08/10) : « Mon équipe » pour un
    membre officiel de l'espace — c'est l'usage courant d'un outil partagé —, sinon « À TerObs »
    (abonné historique sans espace : il n'a pas d'équipe). Le choix reste visible et changeable
    avant l'envoi ; il n'est jamais retenu d'un message à l'autre. Un geste explicite (« Écrire à
    TerObs » du panneau « Comprendre ce chiffre ») impose son propre niveau. */
export function niveauParDefaut(membre: boolean | undefined): Niveau {
  return membre ? 'equipe' : 'terobs'
}

export interface Ancre { page?: string; section?: string; graphique?: string; tuile?: string }

/** L'auteur d'un message ou d'une réponse : « L'équipe TerObs », ou le nom du compte. Jamais
    son adresse côté client ; la boîte de l'équipe reçoit `email` (et `redige_par`, interne). */
export interface AuteurFil {
  nom: string | null; moi: boolean; terobs: boolean; role_espace: string | null
  email?: string | null; redige_par?: string | null
}

export interface ReponseFil {
  id: string; texte: string | null; signature: string; auteur?: AuteurFil
  cree_le: string; modifie_le?: string | null; retire?: boolean
  peut_modifier?: boolean; peut_retirer?: boolean
}
/** Ancien nom (les réponses n'étaient que celles de l'équipe). */
export type ReponseEquipe = ReponseFil

export interface Message {
  id: string; insee: string
  niveau?: Niveau
  // A20 (R1) : l'ancre STOCKÉE ne change jamais. `page_actuelle` / `thematique_actuelle` —
  // servis par le serveur depuis l'arborescence à 20 thématiques — disent où la section
  // ancrée vit AUJOURD'HUI dans le registre actif (`log-14` : stockée sous `log`, servie
  // sous `urb`). Absents (serveur antérieur), on retombe sur la page stockée : voir `pageDe`.
  ancre: { page: string; section: string | null; graphique: string | null; tuile?: string | null
           page_actuelle?: string | null; thematique_actuelle?: string | null }
  page_actuelle?: string | null
  thematique_actuelle?: string | null
  // A20 (lot B) : la section à ouvrir — l'ancrée, ou sa remplaçante (`soc-1` → `rev-1`).
  section_actuelle?: string | null
  contexte: { commune_nom?: string | null; page_titre?: string | null; section_titre?: string | null;
              graphique?: string | null; graphique_titre?: string | null; tuile_titre?: string | null }
  texte: string | null; nature: Nature | null; statut: Statut
  /** « Envoyé », « En cours », « TerObs a répondu », « Traité » — pour un message à TerObs. */
  statut_client: string | null
  cree_le: string; modifie_le?: string | null; retire: boolean; reponses: ReponseFil[]
  auteur?: AuteurFil
  peut_modifier?: boolean; peut_retirer?: boolean; peut_repondre?: boolean; peut_clore?: boolean
  statut_le?: string | null; statut_par?: string | null; statut_par_nom?: string | null
  derniere_activite_le?: string; nombre_messages?: number; dernier_intervenant?: 'client' | 'equipe'
  client?: { organisation: string | null; commune_reference: string | null }
}

const entetes = (token: string | null) => token ? { Authorization: `Bearer ${token}` } : undefined

function erreur(e: unknown, defaut: string): Error {
  const detail = axios.isAxiosError(e) ? (e.response?.data as { detail?: unknown } | undefined)?.detail : undefined
  return new Error(typeof detail === 'string' ? detail : defaut)
}

export async function ecrireCommentaire(insee: string, ancre: Ancre, texte: string,
                                        nature: Nature | null, niveau: Niveau,
                                        token: string | null): Promise<Message> {
  try {
    const r = await axios.post<Message>(`${API}/observatoire/${insee}/commentaires`,
      { ancre, texte, nature: niveau === 'terobs' ? nature : null, niveau },
      { headers: entetes(token) })
    return r.data
  } catch (e) {
    throw erreur(e, 'Le message n’a pas pu être envoyé. Réessayez dans un instant.')
  }
}

/** Une page de « mes commentaires » ; `suivant` (l'identifiant à passer en `avant`) est null à
    la dernière page. `non_lus` porte sur TOUS les messages du compte (et de la commune donnée),
    après le marquage éventuel des fils rendus. */
export interface PageMessages { messages: Message[]; non_lus: number; suivant: string | null }

/** `page` (barre latérale, 06/10) : les seuls fils écrits sur cette page (thématique ou
    « synthese ») ; `niveau` : un seul niveau. Le marquage lu porte seulement sur les fils rendus,
    le compteur restant inclut aussi les fils masqués. `vusJusqua` borne chaque fil à sa dernière
    réponse affichée (tableau vide : aucune réponse vue), dans l'ordre servi par le serveur. */
export async function mesMessages(token: string | null, insee?: string, marquerLus = false,
                                  avant?: string, page?: string, niveau?: Niveau, etat: EtatTickets = 'ouverts',
                                  affiches?: string[], vusJusqua?: string[], signal?: AbortSignal): Promise<PageMessages> {
  try {
    const r = await axios.get<PageMessages>(`${API}/me/commentaires`, {
      headers: entetes(token),
      signal,
      timeout: 15000,
      params: { ...(insee ? { commune: insee } : {}), ...(marquerLus ? { marquer_lus: 1 } : {}),
                ...(avant ? { avant } : {}), ...(page ? { page } : {}), ...(niveau ? { niveau } : {}),
                ...(etat === 'clos' ? { etat } : {}), ...(affiches ? { affiches: affiches.join(',') } : {}),
                ...(vusJusqua ? { vus_jusqua: vusJusqua.join(',') } : {}) },
    })
    return r.data
  } catch (e) {
    throw erreur(e, 'Vos commentaires n’ont pas pu être chargés.')
  }
}

/** Un fil, tel que ce compte le lit ; null s'il n'est plus lisible (retiré, hors de portée). */
export async function lireFil(id: string, token: string | null): Promise<Message | null> {
  try {
    const r = await axios.get<Message>(`${API}/commentaires/${encodeURIComponent(id)}`,
      { headers: entetes(token) })
    return r.data
  } catch (e) {
    // 404 : le fil n'est plus lisible (retiré sans réponse) — ce n'est pas une erreur.
    if ((e as { response?: { status?: number } } | null)?.response?.status === 404) return null
    throw erreur(e, 'Le fil n’a pas pu être rechargé.')
  }
}

/** Répondre dans un fil (au message ou à une réponse : la réponse va sous le message). Rend le
    fil entier, à jour. */
export async function repondreAuFil(id: string, texte: string, token: string | null): Promise<Message> {
  try {
    const r = await axios.post<Message>(`${API}/commentaires/${encodeURIComponent(id)}/reponses`,
      { texte }, { headers: entetes(token) })
    return r.data
  } catch (e) {
    throw erreur(e, 'La réponse n’a pas pu être envoyée.')
  }
}

/** Modifier le texte de son message ou de sa réponse. Rend le fil entier, à jour. */
export async function modifierMessage(id: string, texte: string, token: string | null): Promise<Message> {
  try {
    const r = await axios.patch<Message>(`${API}/commentaires/${encodeURIComponent(id)}`,
      { texte }, { headers: entetes(token) })
    return r.data
  } catch (e) {
    throw erreur(e, 'Le message n’a pas pu être modifié.')
  }
}

export async function cloreTicket(id: string, token: string | null): Promise<Message> {
  try {
    const r = await axios.post<Message>(`${API}/commentaires/${encodeURIComponent(id)}/cloture`, {},
      { headers: entetes(token) })
    return r.data
  } catch (e) {
    throw erreur(e, 'La demande n’a pas pu être clôturée.')
  }
}

export async function retirerMessage(id: string, token: string | null): Promise<void> {
  try {
    await axios.delete(`${API}/commentaires/${encodeURIComponent(id)}`, { headers: entetes(token) })
  } catch (e) {
    throw erreur(e, 'Le message n’a pas pu être retiré.')
  }
}

// ── Boîte de l'équipe (administrateurs de plateforme) ──────────────────────────────────────
export interface Boite { messages: Message[]; comptes: Partial<Record<Statut, number>>; suivant: string | null }

export async function lireBoite(token: string | null,
                                filtres: { statut?: Statut | 'ouverts' | 'tous'; commune?: string; nature?: Nature; client?: string; q?: string;
                                           avant?: string }): Promise<Boite> {
  try {
    const params = Object.fromEntries(Object.entries(filtres).filter(([, v]) => v))
    const r = await axios.get<Boite>(`${API}/admin/commentaires`, { headers: entetes(token), params })
    return r.data
  } catch (e) {
    throw erreur(e, 'La boîte n’a pas pu être chargée.')
  }
}

export async function changerStatut(id: string, statut: Statut | 'restaurer', token: string | null): Promise<void> {
  try {
    await axios.patch(`${API}/admin/commentaires/${encodeURIComponent(id)}`, { statut },
      { headers: entetes(token) })
  } catch (e) {
    throw erreur(e, 'Le statut n’a pas pu être changé.')
  }
}

export async function repondre(id: string, texte: string, token: string | null): Promise<void> {
  try {
    await axios.post(`${API}/admin/commentaires/${encodeURIComponent(id)}/reponses`, { texte },
      { headers: entetes(token) })
  } catch (e) {
    throw erreur(e, 'La réponse n’a pas pu être enregistrée.')
  }
}

/** « 06/10/2026 à 14 h 05 », lu à Paris. */
export function dateHeure(iso: string): string {
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return iso
  const j = d.toLocaleDateString('fr-FR', { timeZone: 'Europe/Paris' })
  const h = d.toLocaleTimeString('fr-FR', { timeZone: 'Europe/Paris', hour: '2-digit', minute: '2-digit' })
  return `${j} à ${h.replace(':', ' h ')}`
}

/**
 * La page où l'ancre d'un message vit AUJOURD'HUI (A20) : `page_actuelle` servie (au niveau
 * du message ou de l'ancre), sinon la page stockée. La synthèse reste la synthèse.
 */
export function pageDe(m: Message): string {
  if (m.ancre.page === 'synthese') return 'synthese'
  return m.page_actuelle || m.ancre.page_actuelle || m.thematique_actuelle
    || m.ancre.thematique_actuelle || m.ancre.page
}

/** Le lien profond qui rouvre l'élément commenté (A20 : sur la page où il vit aujourd'hui) :
    la page, l'élément précis en paramètre (`graphique=` ou `tuile=`, que la page cherche une
    fois chargée), et la section en ancre — que la page sait aussi rediriger (A20, R4/R7). */
export function lienMessage(m: Message, synthese = '/synthese'): string {
  const page = pageDe(m)
  const precis = m.ancre.graphique ? `?graphique=${encodeURIComponent(m.ancre.graphique)}`
    : m.ancre.tuile ? `?tuile=${encodeURIComponent(m.ancre.tuile)}` : ''
  if (page === 'synthese') return `/o/${m.insee}${synthese}${precis}`
  // La section servie d'abord : un commentaire sur `soc-1` (retirée) rouvre `/t/rev#rev-1`.
  const section = m.section_actuelle || m.ancre.section
  return `/o/${m.insee}/t/${page}${precis}${section ? `#${section}` : ''}`
}

/** Où le message a été écrit, en clair : « Démographie › Population : effectif et évolution ›
    Population aux recensements » (page › section › graphique ou tuile). Le titre du graphique
    est celui que le SERVEUR a retrouvé dans le code des builders ; un graphique au titre
    composé à l'exécution reste « Graphique ». */
export function lieu(m: Message): string {
  const c = m.contexte
  const element = m.ancre.graphique ? (c.graphique_titre || 'Graphique')
    : (m.ancre.tuile || c.tuile_titre || null)
  return [c.page_titre || m.ancre.page, c.section_titre || m.ancre.section, element]
    .filter(Boolean).join(' › ')
}

/** Le rôle d'un membre dans l'espace, en clair. */
export function roleLisible(role: string | null | undefined): string | null {
  if (!role) return null
  return ({ admin: 'administrateur de l’espace', executif: 'exécutif', agent: 'agent' } as
    Record<string, string>)[role] ?? role
}
